import React from "react";
import moment from "moment";
import * as Icon from "react-feather";

import WComponent from "../../common/WComponent";
import DeleteConfirmationModal from "../../common/modal/confirmation_modal/ConfirmationModal";
import EngagementService from "../../../services/EngagementService";
import UtilityService from "../../../services/UtilityService";

const { alert } = UtilityService;

/**
 * Lists the one-time tasks and notes queued on a recurring engagement, waiting to be created
 * on its next materialized occurrence, and lets the user discard them before that happens.
 */
class EngagementQueuedItems extends WComponent {
	constructor() {
		super();
		this.state = {
			removing: false,
			pendingRemoveId: null,
			pendingRemoveKind: null
		};
	}

	getSnapshot = () => {
		let { recurringEngagement } = this.props;

		try {
			return JSON.parse(recurringEngagement.latest_engagement_snapshot || recurringEngagement.engagement_snapshot) || {};
		} catch (error) {
			console.log(error);
			return {};
		}
	};

	onRemoveClick = e => {
		let { itemId, itemKind } = e.currentTarget.dataset;

		this.update({
			pendingRemoveId: itemId,
			pendingRemoveKind: itemKind
		});
	};

	onRemoveConfirmClose = async ({ confirmed }) => {
		let { pendingRemoveId, pendingRemoveKind, removing } = this.state;
		let { recurringEngagement, onChange } = this.props;

		await this.update({ pendingRemoveId: null, pendingRemoveKind: null });

		if (!confirmed || removing) {
			return;
		}

		await this.update({ removing: true });

		let updatedRecurringEngagement = await EngagementService.removeQueuedRecurringEngagementItems({
			recurringEngagementId: recurringEngagement.id,
			taskIds: pendingRemoveKind === "task" ? [pendingRemoveId] : undefined,
			noteIds: pendingRemoveKind === "note" ? [pendingRemoveId] : undefined
		});

		await this.update({ removing: false });

		if (!updatedRecurringEngagement) {
			alert({ type: "error", text: "Failed to remove the queued item." });
			return;
		}

		alert({ type: "success", text: "Removed. It will not be added to the next recurring engagement." });

		if (onChange) {
			onChange(updatedRecurringEngagement);
		}
	};

	renderItem = ({ id, kind, title, subtitle }) => {
		return (
			<div className="engagement__queued__item" key={id}>
				<div className="engagement__queued__item__body">
					<div className="engagement__queued__item__body__title">{title}</div>
					<div className="engagement__queued__item__body__subtitle">{subtitle}</div>
				</div>
				<Icon.X className="engagement__queued__item__remove" size={16} data-item-id={id} data-item-kind={kind} onClick={this.onRemoveClick} />
			</div>
		);
	};

	render() {
		let { pendingRemoveId, pendingRemoveKind } = this.state;
		let snapshot = this.getSnapshot();
		let queuedTasks = snapshot.oneTimeTasks || [];
		let queuedNotes = snapshot.oneTimeNotes || [];

		if (queuedTasks.length === 0 && queuedNotes.length === 0) {
			return null;
		}

		return (
			<div className="engagement__queued">
				<div className="engagement__queued__title">Queued for the next engagement</div>
				<div className="engagement__queued__hint">
					These one-time items will be added to the next engagement generated by this recurrence, then removed from this list.
				</div>
				{queuedTasks.map(task =>
					this.renderItem({
						id: task.id,
						kind: "task",
						title: task.name,
						subtitle: `Task${task.deadline ? ` · due ${moment(task.deadline).format("MMMM D, YYYY")}` : ""} · queued ${moment(task.queuedAt).format(
							"MMMM D, YYYY"
						)}`
					})
				)}
				{queuedNotes.map(note =>
					this.renderItem({
						id: note.id,
						kind: "note",
						title: note.content,
						subtitle: `${UtilityService.getNoteType({ capitalizeFirstLetter: true, type: note.type })} · queued ${moment(note.queuedAt).format(
							"MMMM D, YYYY"
						)}`
					})
				)}

				<DeleteConfirmationModal
					title={`Remove Queued ${pendingRemoveKind === "note" ? "Note" : "Task"}?`}
					warning="It will not be added to the next recurring engagement. This cannot be undone."
					show={!!pendingRemoveId}
					onClose={this.onRemoveConfirmClose}
				/>
			</div>
		);
	}
}

export default EngagementQueuedItems;
